/**
 * Cross-currency split distribution — the shared rounding rule for the balance
 * engines on web (this file, Number) and mobile (`src/utils/split-distribution.ts`,
 * BigInt).
 *
 * When an expense is in a currency other than the group currency, its total is
 * converted to the group currency ONCE and then redistributed across the splits in
 * proportion to each split's source-currency minor amount. The mobile (RN) and web
 * balance engines MUST round each share identically, otherwise the same data
 * produces different per-member balances on each platform.
 *
 * Rounding rule: round half up — `Math.round(value * total / divisor)`. The mobile
 * BigInt engine implements the same rule as `(value * total + divisor / 2) / divisor`,
 * which is provably equal for non-negative integers (a `.5` boundary can only occur
 * when `divisor` is even, where `divisor / 2` is exact). Keep the two in sync.
 *
 * The denominator is the SUM of the splits' source minor amounts (not the stored
 * `amount_minor_src`), so the proportions always sum to exactly 1 even when the
 * split rows do not add up to the expense total (e.g. rounded imported shares).
 * The last split absorbs the rounding remainder so the parts sum exactly to `total`.
 */

/** Round-half-up proportional share (minor units) of `total` for one split. */
export function proportionalShareMinor(
  splitSrcMinor: number,
  total: number,
  totalSrcMinor: number
): number {
  if (totalSrcMinor <= 0) return 0;
  return Math.round((splitSrcMinor * total) / totalSrcMinor);
}

/**
 * Distribute `total` (group-currency minor units) across the given splits in
 * proportion to their source-currency minor amounts. Returns one amount per split,
 * in the same order; the last split absorbs the rounding remainder so the returned
 * amounts always sum exactly to `total`.
 */
export function distributeCrossCurrencyMinor(
  splitSrcMinors: number[],
  total: number
): number[] {
  const totalSrcMinor = splitSrcMinors.reduce((sum, v) => sum + v, 0);
  const result: number[] = [];
  let distributed = 0;
  for (let i = 0; i < splitSrcMinors.length; i++) {
    if (i === splitSrcMinors.length - 1) {
      result.push(total - distributed);
    } else {
      const share = proportionalShareMinor(splitSrcMinors[i], total, totalSrcMinor);
      result.push(share);
      distributed += share;
    }
  }
  return result;
}
